import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { useBillingReconciliationHistoryBacLookup } from '../../hooks/api/useBillingReconciliationHooks.js';
import { copyToClipboard } from '../../utils/resultsTableFormat.js';

const money = (value) => Number(value || 0).toLocaleString(undefined, {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const monthLabel = (value) => {
  if (!value) return '--';
  const [year, month] = String(value).split('-');
  return new Date(Number(year), Number(month || 1) - 1, 1).toLocaleDateString(undefined, {
    month: 'short',
    year: 'numeric',
  });
};

const actionLabel = (value = '') => String(value || '')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const csvCell = (value) => {
  const text = String(value ?? '');
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const finalRowsCsv = (rows = []) => {
  const columns = [
    ['BAC', 'bac'],
    ['Dealer Name', 'dealerName'],
    ['Product', (row) => row.productName || row.productSelection || ''],
    ['Product Code', 'productCode'],
    ['Status', 'productStatus'],
    ['Effective Date', 'effectiveDate'],
    ['Dealer Cost', 'dealerCost'],
    ['Notes', (row) => row.notes || row.source || ''],
  ];
  return [
    columns.map(([label]) => csvCell(label)).join(','),
    ...rows.map((row) => columns.map(([, key]) => csvCell(
      typeof key === 'function' ? key(row) : row[key]
    )).join(',')),
  ].join('\n');
};

const writeClipboardText = async (value) => {
  const copied = await copyToClipboard(value);
  if (!copied) {
    throw new Error('Unable to copy final billing rows.');
  }
};

const SummaryTile = ({ label, value }) => (
  <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1, minWidth: 160 }}>
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, textTransform: 'uppercase' }}>
      {label}
    </Typography>
    <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>{value}</Typography>
  </Paper>
);

const FinalRowsTable = ({ rows = [] }) => (
  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1, maxHeight: 280 }}>
    <Table stickyHeader size="small" aria-label="Final file billing rows">
      <TableHead>
        <TableRow>
          <TableCell>Product</TableCell>
          <TableCell>Code</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Effective</TableCell>
          <TableCell align="right">Dealer cost</TableCell>
          <TableCell>Notes</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, index) => (
          <TableRow key={`${row.rowNumber || index}-${row.productCode || index}`}>
            <TableCell>
              <Stack spacing={0.25}>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>{row.productName || row.productSelection || '--'}</Typography>
                <Typography variant="caption" color="text.secondary">{row.dealerName || '--'}</Typography>
              </Stack>
            </TableCell>
            <TableCell>{row.productCode || '--'}</TableCell>
            <TableCell>{row.productStatus || '--'}</TableCell>
            <TableCell>{row.effectiveDate || '--'}</TableCell>
            <TableCell align="right">{money(row.dealerCost)}</TableCell>
            <TableCell sx={{ maxWidth: 280 }}>
              <Typography variant="caption" color="text.secondary" noWrap title={row.notes || row.source || ''}>
                {row.notes || row.source || '--'}
              </Typography>
            </TableCell>
          </TableRow>
        ))}
        {!rows.length && (
          <TableRow>
            <TableCell colSpan={6} sx={{ color: 'text.secondary' }}>No final billing rows found for this BAC.</TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

const ActionsTable = ({ actions = [] }) => (
  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1, maxHeight: 280 }}>
    <Table stickyHeader size="small" aria-label="Actions for BAC">
      <TableHead>
        <TableRow>
          <TableCell>Action</TableCell>
          <TableCell>Lane</TableCell>
          <TableCell>Product</TableCell>
          <TableCell>Replacement</TableCell>
          <TableCell>Price</TableCell>
          <TableCell>Note / fix</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {actions.map((action) => (
          <TableRow key={action.id || `${action.actionType}-${action.sourceProductCode || action.productCode}-${action.createdAt || ''}`}>
            <TableCell>
              <Stack spacing={0.25}>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>{actionLabel(action.actionType)}</Typography>
                <Typography variant="caption" color="text.secondary">{action.status || '--'}</Typography>
              </Stack>
            </TableCell>
            <TableCell>{action.exportLane || '--'}</TableCell>
            <TableCell>{action.sourceProductCode || action.productCode || '--'}</TableCell>
            <TableCell>{action.replacementProductCode || '--'}</TableCell>
            <TableCell>{[action.originalPrice, action.proposedPrice ?? action.customPrice].filter((value) => value !== null && value !== undefined && value !== '').map(money).join(' -> ') || '--'}</TableCell>
            <TableCell sx={{ maxWidth: 320 }}>
              <Typography variant="caption" color="text.secondary" noWrap title={action.fix || action.note || action.problem || ''}>
                {action.fix || action.note || action.problem || '--'}
              </Typography>
            </TableCell>
          </TableRow>
        ))}
        {!actions.length && (
          <TableRow>
            <TableCell colSpan={6} sx={{ color: 'text.secondary' }}>No actions found for this BAC.</TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

const HistoryBacLookupDialog = ({ open, row, onClose }) => {
  const [bac, setBac] = useState('');
  const [submittedBac, setSubmittedBac] = useState('');
  const [copyMessage, setCopyMessage] = useState('');
  const [copyError, setCopyError] = useState('');

  useEffect(() => {
    if (!open) {
      setBac('');
      setSubmittedBac('');
      setCopyMessage('');
      setCopyError('');
    }
  }, [open]);

  useEffect(() => {
    setSubmittedBac('');
    setCopyMessage('');
    setCopyError('');
  }, [row?.runId]);

  const lookupQuery = useBillingReconciliationHistoryBacLookup({
    runId: row?.runId,
    bac: submittedBac,
    limit: 500,
  }, {
    enabled: open && Boolean(row?.runId && submittedBac),
  });
  const lookupResult = lookupQuery.data;
  const result = (
    lookupResult
    && String(lookupResult.runId || '') === String(row?.runId || '')
    && String(lookupResult.bac || '') === String(submittedBac || '')
  ) ? lookupResult : null;
  const submit = (event) => {
    event.preventDefault();
    setCopyMessage('');
    setCopyError('');
    setSubmittedBac(bac.trim());
  };
  const copyFinalRows = async () => {
    const rows = result?.finalRows || [];
    if (!rows.length) return;
    setCopyMessage('');
    setCopyError('');
    try {
      await writeClipboardText(finalRowsCsv(rows));
      setCopyMessage(`Copied ${rows.length.toLocaleString()} final row${rows.length === 1 ? '' : 's'} for BAC ${result.bac} as CSV.`);
    } catch (error) {
      setCopyError(error.message || 'Unable to copy final billing rows.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack spacing={0.5}>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>BAC billing lookup</Typography>
          <Typography variant="body2" color="text.secondary">
            {monthLabel(row?.serviceMonth)} · Round {row?.roundNumber || '--'}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Box component="form" onSubmit={submit}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              <TextField
                autoFocus
                label="BAC"
                size="small"
                value={bac}
                onChange={(event) => setBac(event.target.value)}
                inputProps={{ 'aria-label': 'History BAC lookup value' }}
              />
              <Button
                type="submit"
                variant="contained"
                startIcon={<SearchOutlinedIcon />}
                disabled={!bac.trim() || lookupQuery.isFetching}
              >
                Search
              </Button>
            </Stack>
          </Box>
          {lookupQuery.isFetching && <LinearProgress />}
          {lookupQuery.error && <Alert severity="error">{lookupQuery.error.message}</Alert>}
          {copyMessage && <Alert severity="success">{copyMessage}</Alert>}
          {copyError && <Alert severity="error">{copyError}</Alert>}
          {result && (
            <>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <SummaryTile label="Final billed" value={money(result.finalBillingAmount)} />
                <SummaryTile label="Final rows" value={Number(result.finalRowCount || 0).toLocaleString()} />
                <SummaryTile label="Actions" value={Number(result.actionCount || 0).toLocaleString()} />
              </Stack>
              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Final file billing rows</Typography>
                  <Tooltip title="Copy final billing rows as CSV">
                    <span>
                      <IconButton
                        aria-label="Copy final billing rows CSV"
                        size="small"
                        onClick={copyFinalRows}
                        disabled={lookupQuery.isFetching || !result.finalRows?.length}
                      >
                        <ContentCopyOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
                <FinalRowsTable rows={result.finalRows || []} />
              </Stack>
              <Divider />
              <Stack spacing={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Actions for BAC</Typography>
                <ActionsTable actions={result.actions || []} />
              </Stack>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default HistoryBacLookupDialog;
